import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

// --- Sent invite schema ---

export const sentInviteSchema = z.object({
  id: z.string(),
  type: z.enum(["workspace", "channel", "client_space", "dm", "group_dm"]),
  token: z.string(),
  workspace_id: z.string().nullable(),
  invite_url: z.string(),
  invited_email: z.string().nullable(),
  target_user_name: z.string().nullable(),
  context_name: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string(),
  use_count: z.number(),
  max_uses: z.number().nullable(),
  revoked: z.boolean(),
  accepted: z.boolean(),
});

export type SentInvite = z.infer<typeof sentInviteSchema>;

export const listSentInvitesResponseSchema = z.object({
  invites: z.array(sentInviteSchema),
});

// --- Received invite schema ---

export const receivedInviteSchema = z.object({
  id: z.string(),
  type: z.enum(["workspace", "channel", "client_space", "dm", "group_dm"]),
  token: z.string(),
  inviter_name: z.string(),
  inviter_avatar_url: z.string().nullable(),
  context_name: z.string().nullable(),
  workspace_name: z.string().nullable(),
  /** Workspace logo for context on channel/workspace invites (recipient needs to know which workspace). */
  workspace_avatar_url: z.string().nullable().optional(),
  workspace_slug: z.string().nullable().optional(),
  scope: z.enum(["individual", "workspace"]).nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  created_at: z.string(),
  expires_at: z.string(),
  status: z.enum(["pending", "accepted", "expired", "revoked"]),
});

export type ReceivedInvite = z.infer<typeof receivedInviteSchema>;

export const listReceivedInvitesResponseSchema = z.object({
  invites: z.array(receivedInviteSchema),
});

// --- Path builders ---

export function listSentInvitesPath(): string {
  return "/api/invites/sent";
}

export function listReceivedInvitesPath(status?: string): string {
  return status
    ? `/api/invites/received?status=${encodeURIComponent(status)}`
    : "/api/invites/received";
}

export function revokeInvitePath(id: string): string {
  return `/api/invites/${encodeURIComponent(id)}/revoke`;
}

export function bulkRevokeInvitesPath(): string {
  return `/api/invites/bulk-revoke`;
}

export const bulkRevokeInvitesBodySchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100),
});

export const bulkRevokeInvitesResponseSchema = z.object({
  revoked_ids: z.array(z.string()),
});

export type BulkRevokeInvitesResponse = z.infer<
  typeof bulkRevokeInvitesResponseSchema
>;

export function declineInvitePath(id: string): string {
  return `/api/invites/${encodeURIComponent(id)}/decline`;
}

// --- Client request helpers ---

function buildAuthHeaders(accessToken: string): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
}

export async function listSentInvitesRequest({
  apiBaseUrl,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<SentInvite[]> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${listSentInvitesPath()}`,
    { headers: buildAuthHeaders(accessToken) },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to list sent invites");
  }

  const payload = await response.json().catch(() => null);
  const parsed = listSentInvitesResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data.invites;
}

export async function listReceivedInvitesRequest({
  apiBaseUrl,
  accessToken,
  status,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  status?: string;
  fetchImpl?: FetchLike;
}): Promise<ReceivedInvite[]> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${listReceivedInvitesPath(status)}`,
    { headers: buildAuthHeaders(accessToken) },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to list received invites");
  }

  const payload = await response.json().catch(() => null);
  const parsed = listReceivedInvitesResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data.invites;
}

export async function revokeInviteRequest({
  apiBaseUrl,
  inviteId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  inviteId: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<void> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${revokeInvitePath(inviteId)}`,
    { method: "POST", headers: buildAuthHeaders(accessToken) },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to revoke invite");
  }
}

export async function bulkRevokeInvitesRequest({
  apiBaseUrl,
  ids,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  ids: string[];
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<BulkRevokeInvitesResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${bulkRevokeInvitesPath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify({ ids }),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to revoke invites");
  }

  const payload = await response.json().catch(() => null);
  const parsed = bulkRevokeInvitesResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function declineInviteRequest({
  apiBaseUrl,
  inviteId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  inviteId: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<void> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${declineInvitePath(inviteId)}`,
    { method: "POST", headers: buildAuthHeaders(accessToken) },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to decline invite");
  }
}
